import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import LocationPicker from "../LocationPicker";

const center = {
  lat: 40.0853,
  lng: 22.3584,
};

const Container = styled.div`
  margin: 50px;
  padding: 2rem;
  background: #fff;
  box-shadow: 0 4px 10px rgba(0,0,0,0.1);
  border-radius: 8px;
 
  @media (max-width: 768px) {
   text-align:center;
  }

`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;

    @media (max-width: 768px) {
    flex-direction:column;
    gap:25px;
  }
`;

const Title = styled.h2`
  color: #385963;
`;

const BackButton = styled.button`
  background: none;
  border: 1px solid #385963;
  color: #385963;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background-color: #f0f0f0;
  }

   @media (max-width: 768px) {
    font-size:10px;
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FieldWrapper = styled.div``;

const FieldTitle = styled.h4`
  margin-bottom: 1rem;
  color: #385963;
`;

const LangRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const LocationRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 1.5rem;

  label {
    display: block;
    font-weight: bold;
    margin-bottom: 0.5rem;
    color: #385963;
  }

  input, textarea {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ccc;
    border-radius: 6px;
    font-size: 1rem;
  }

  textarea {
    resize: vertical;
    min-height: 100px;
  }
`;

const Button = styled.button`
  background-color: #385963;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  border-radius: 6px;
  cursor: pointer;
  margin-top: 2rem;
  place-self: center;

  &:hover {
    background-color: #2c4a52;
  }
`;

const DeleteButton = styled.button`
  background-color: #ff4d4d;  /* Red background */
  color: white;
  border: none;
  padding: 0.5rem 1rem;  /* Smaller padding */
  font-size: 0.9rem;      /* Smaller font size */
  border-radius: 6px;
  cursor: pointer;
  margin-top: 10px;
  
  &:hover {
    background-color: #e60000;  /* Darker red on hover */
  }
`;

// Modal to display the full image
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  visibility: ${(props) => (props.visible ? 'visible' : 'hidden')};
`;

const ModalImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
`;

const PhotoGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PhotoItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  img{
    border-radius: 5px;
  }

    @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
  
`;


const SinglePhotoContainer = styled.div`
  display: flex;
  flex-direction:column;
  align-items: center;
  margin-top: 10px;

  img{
    border-radius: 5px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
  background-color: white;
  color: #385963;

  &:focus {
    border-color: #385963;
    outline: none;
    box-shadow: 0 0 0 2px rgba(56, 89, 99, 0.2);
  }

  option {
    color: #385963;
  }
`;

// FilePlaceTypeEnum
const FILE_PLACE_TYPE = {
  Gallery: 0,
  CategoryPhoto: 1,
  CoverPhoto: 2,
  Poster: 3,
};

const EditExperience = () => {

  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const tokenString = sessionStorage.getItem("token");
  const { id } = useParams();
  const { t , i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalImage, setModalImage] = useState('');
  const [categories, setCategories] = useState([]);

  const [filesToDelete, setFilesToDelete] = useState([]);

  // Initialize form with default structure to avoid undefined errors
  const [form, setForm] = useState({
    name: { gr: '', en: '' },
    description: { gr: '', en: '' },
    location: { latitude: '', longitude: '' },
    link360View: '',
    panoramicView: '',
    videoLink:'',
    categoryId: '',
    coverPhoto: null,
    categoryPhoto: null,
    photos: [],
    existingCoverFileName: null,
    existingCategoryFileName: null,
    // Add existing photos filenames if needed to handle delete on server
    existingGalleryFileNames: [], // optional
  });

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: "AIzaSyCIrKrxTVDqlcRVFNyNMm5iS869G7RYvuc",
    libraries: ["places"],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
         const response = await fetch(`${BASE_URL}Experience/${id}`);
        if (!response.ok) throw new Error("Failed to fetch experience data");
        const data = await response.json();
        const rawFileNames = data.fileNames; 
        let fetchedFiles = [];

      if (rawFileNames.length > 0) {
        const fileResponse = await fetch(`${BASE_URL}Experience/getFiles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenString}`,
          },
          body: JSON.stringify( rawFileNames),
        });

        if (!fileResponse.ok) throw new Error("Failed to fetch file contents");
        fetchedFiles = await fileResponse.json();

      }

      // Process fetched files based on their filePlaceType
      const {
        existingCoverFileName,
        existingCategoryFileName,
        existingGalleryFileNames, 
        galleryBase64Srcs,
        coverBase64Src,
        categoryBase64Src,
      } = processFiles(fetchedFiles);

      setForm({
        name: data.name || { gr: '', en: '' },
        description: data.description || { gr: '', en: '' },
        location: data.location || { latitude: '', longitude: '' },
        videoLink: data.videoLink || '',
        categoryId: data.categoryId || '',
        coverPhoto: null,
        categoryPhoto: null,
        photos: [],
        existingCoverFileName,
        existingCategoryFileName,
        existingGalleryFileNames,
        galleryBase64Srcs,
        coverBase64Src,
        categoryBase64Src,
      });

      } catch (err) {
        console.error('Error loading Experience:', err);
      }
       finally {
          setIsLoading(false); // End loading
        }
    };
    fetchData();
  }, [BASE_URL, id, tokenString]);


    useEffect(() => {
      const fetchCategories = async () => {
        try {
          const response = await fetch(`${BASE_URL}ExperienceCategory`);
          const data = await response.json();
          setCategories(data);
        } catch (error) {
          console.error('Error fetching Categories:', error);
        }
      };
  
      fetchCategories();
    }, []);

  const uploadFiles = async (id, files, types) => {

 const formData = new FormData();
 
 for (let i = 0; i < files.length; i++) {
   // Append the file name (string)
   formData.append('uploadFiles', files[i]);
 
   // Append the corresponding file type
   formData.append('uploadFileTypes', types[i].toString());
 }

  try {
    const response = await fetch(`${BASE_URL}Experience/${id}/uploadFiles`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokenString}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Upload failed:', errorText);
      throw new Error(errorText);
    }

  } catch (error) {
    console.error('Upload error:', error);
  }
};

const processFiles = (files) => {
  const coverFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CoverPhoto);
  const categoryFile = files.find(f => f.filePlaceType === FILE_PLACE_TYPE.CategoryPhoto);
  const galleryFiles = files.filter(f => f.filePlaceType === FILE_PLACE_TYPE.Gallery);

  const coverBase64Src = coverFile ? `data:${coverFile.mimeType};base64,${coverFile.base64Content}` : null;
  const categoryBase64Src = categoryFile ? `data:${categoryFile.mimeType};base64,${categoryFile.base64Content}` : null;
  const galleryBase64Srcs = galleryFiles.map(f => `data:${f.mimeType};base64,${f.base64Content}`);

  // Collect filenames of gallery files to handle deletions later
  const existingGalleryFileNames = galleryFiles.map(f => f.fileName);

  return {
    existingCoverFileName: coverFile?.fileName || null,
    existingCategoryFileName: categoryFile?.fileName || null,
     existingGalleryFileNames,  
    galleryBase64Srcs,
    coverBase64Src,
    categoryBase64Src,
  };
};

  const deleteFiles = async (id, fileNames) => {
    try {
      const response = await fetch(`${BASE_URL}Experience/${id}/deleteFiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenString}`,
        },
        body: JSON.stringify(fileNames),
      });

      if (!response.ok) throw new Error('Failed to delete files');
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const handleChange = (field, value, lang = null) => {
    if (lang) {
      setForm(prev => ({
        ...prev,
        [field]: {
          ...prev[field],
          [lang]: value,
        },
      }));
    } else {
      setForm(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleFileChange = (e, isMultiple, field) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    if (isMultiple) {
      setForm(prev => ({
        ...prev,
        photos: [...prev.photos, ...Array.from(files)],
      }));
    } else if (field) {
      setForm(prev => ({
        ...prev,
        [field]: files[0],
      }));
    }
  };

  const handleOpenModal = (img) => {
    if (!img) return;
    setModalImage(typeof img === 'string' ? img : URL.createObjectURL(img));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setModalImage('');
  };

  // Delete cover photo (new or existing)
const handleDeleteCoverPhoto = () => {
  if (form.existingCoverFileName) {
    setFilesToDelete(prev => [...prev, form.existingCoverFileName]);
  }
  setForm(prev => ({
    ...prev,
    coverPhoto: null,
    coverBase64Src: null,
    existingCoverFileName: null,
  }));
};

// Delete category photo (new or existing)
const handleDeleteCategoryPhoto = () => {
  if (form.existingCategoryFileName) {
    setFilesToDelete(prev => [...prev, form.existingCategoryFileName]);
  }
  setForm(prev => ({
    ...prev,
    categoryPhoto: null,
    categoryBase64Src: null,
    existingCategoryFileName: null,
  }));
};

// Delete new gallery photo (not uploaded yet)
const handleDeleteNewGalleryPhoto = (index) => {
  setForm(prev => ({
    ...prev,
    photos: prev.photos.filter((_, i) => i !== index),
  }));
};

// Delete existing gallery photo (already uploaded)
const handleDeleteExistingGalleryPhoto = (index) => {
  const fileName = form.existingGalleryFileNames?.[index];
  if (!fileName) return;

  setFilesToDelete(prev => [...prev, fileName]);
  setForm(prev => {
    const newGalleryBase64Srcs = [...prev.galleryBase64Srcs];
    newGalleryBase64Srcs.splice(index, 1);

    const newExistingGalleryFileNames = [...prev.existingGalleryFileNames];
    newExistingGalleryFileNames.splice(index, 1);

    return {
      ...prev,
      galleryBase64Srcs: newGalleryBase64Srcs,
      existingGalleryFileNames: newExistingGalleryFileNames,
    };
  });
};


  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedΕxperience = {
      name: form.name,
      description: form.description,
      location: form.location,
      link360View: form.link360View,
      videoLink: form.videoLink,
      panoramicView: form.panoramicView,
      categoryId: form.categoryId,
    };

    try {
      const response = await fetch(`${BASE_URL}Experience/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenString}`,
        },
        body: JSON.stringify(updatedΕxperience),
      });

      if (!response.ok) throw new Error('Failed to update experience');

    
      if(response.ok){

      // Delete old cover photo if replacing
      if (form.coverPhoto && form.existingCoverFileName) {
        await deleteFiles(id, [form.existingCoverFileName]);
      }
      // Delete old category photo if replacing
      if (form.categoryPhoto && form.existingCategoryFileName) {
        await deleteFiles(id, [form.existingCategoryFileName]);
      }

      if (filesToDelete.length > 0) {
      await deleteFiles(id, filesToDelete);
    }

      // Upload files
      const uploadFileList = [];
      const fileTypes = [];

      if (form.coverPhoto) {
        uploadFileList.push(form.coverPhoto);
        fileTypes.push(FILE_PLACE_TYPE.CoverPhoto);
      }
      if (form.categoryPhoto) {
        uploadFileList.push(form.categoryPhoto);
        fileTypes.push(FILE_PLACE_TYPE.CategoryPhoto);
      }
      if (form.photos.length > 0) {
        uploadFileList.push(...form.photos);
        fileTypes.push(...Array(form.photos.length).fill(FILE_PLACE_TYPE.Gallery));
      }

      if (uploadFileList.length > 0) {
        await uploadFiles(id, uploadFileList, fileTypes);
  
      }

    if (form.gpxFile) {
      const gpxForm = new FormData();
      gpxForm.append('file', form.gpxFile);
     
      try {
        const gpxResponse = await fetch(`${BASE_URL}Experience/${id}/uploadGpx`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${tokenString}`,
          },
          body: gpxForm,
        });
     
        if (!gpxResponse.ok) {
          const errorText = await gpxResponse.text();
          alert(t('uploadGpxFailed'));
          throw new Error(errorText);
        }
      } catch (gpxError) {
        console.error('Error uploading GPX file:', gpxError);
      }
    }

      alert(t('updatedSuccessfully'));
      navigate('/admin/experiences');
    }
    } catch (err) {
      console.error(err);
      alert(t('errorUpdatingexperience'));
    }
  };

  if (!form) return <p>{t('loading')}...</p>;

  return (
    <Container>
      <TopBar>
        <BackButton onClick={() => navigate('/admin/experiences')}>
          ← {t('backToExperiences')}
        </BackButton>
        <Title>{t('editExperience')}</Title>
      </TopBar>
      <form onSubmit={handleSubmit}>
        <FormWrapper>
          {['name', 'description'].map((field) => (
            <FieldWrapper key={field}>
              <FieldTitle>{t(field)}</FieldTitle>
              <LangRow>
                <FieldGroup>
                  <label>{t(field)} (Ελληνικά)</label>
                  {field === 'description' ? (
                    <textarea
                      value={form[field].gr}
                      onChange={(e) => handleChange(field, e.target.value, 'gr')}
                    />
                  ) : (
                    <input
                      type="text"
                      value={form[field].gr}
                      onChange={(e) => handleChange(field, e.target.value, 'gr')}
                    />
                  )}
                </FieldGroup>

                <FieldGroup>
                  <label>{t(field)} (English)</label>
                  {field === 'description' ? (
                    <textarea
                      value={form[field].en}
                      onChange={(e) => handleChange(field, e.target.value, 'en')}
                    />
                  ) : (
                    <input
                      type="text"
                      value={form[field].en}
                      onChange={(e) => handleChange(field, e.target.value, 'en')}
                    />
                  )}
                </FieldGroup>
              </LangRow>
            </FieldWrapper>
          ))}

          <FieldWrapper>
            <FieldTitle>{t('category')}</FieldTitle>
            <FieldGroup>
              <StyledSelect
                value={form.categoryId}
                onChange={(e) => handleChange('categoryId', e.target.value)}
                required
              >
                <option value="" disabled>
                  {t('selectCategory')}
                </option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name?.[i18n.language] || cat.name?.en}
                  </option>
                ))}
              </StyledSelect>
            </FieldGroup>
          </FieldWrapper>

        <FieldWrapper>
            <FieldTitle>{t("location")}</FieldTitle>
            <LocationPicker
              location={form.location}
              onChange={(loc) => handleChange("location", loc)}
              isLoaded={isLoaded}
            />
         </FieldWrapper>


         <FieldGroup>
            <label>{t('videoLink')}</label>
            <input
              type="text"
              value={form.videoLink}
              onChange={(e) => handleChange('videoLink', e.target.value)}
            />
          </FieldGroup>

          <FieldGroup>
           <FieldTitle>{t('experiencePath')} (.gpx)</FieldTitle>
           <input
             type="file"
             accept=".gpx"
             onChange={(e) => handleChange('gpxFile', e.target.files[0])}
           />
          </FieldGroup>
          {form.gpxFile && <p>{form.gpxFile.name}</p>}


          <FieldWrapper>
             <FieldGroup>
            <FieldTitle>{t('coverPhoto')}</FieldTitle>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, false, 'coverPhoto')}
            />
            </FieldGroup>
            {isLoading ? (
                <p>Loading...</p>
              ) : form.coverPhoto || form.coverBase64Src ? (
                <SinglePhotoContainer>
                  <img
                    src={
                      form.coverPhoto
                        ? URL.createObjectURL(form.coverPhoto)
                        : form.coverBase64Src
                    }
                    alt="Cover"
                    style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                    onClick={() =>
                      handleOpenModal(
                        form.coverPhoto
                          ? URL.createObjectURL(form.coverPhoto)
                          : form.coverBase64Src
                      )
                    }
                  />
                  <DeleteButton type="button" onClick={handleDeleteCoverPhoto}>
                    {t('delete')}
                  </DeleteButton>
                </SinglePhotoContainer>
              ) : (
                <p>{t('noFiles')}</p> 
              )}
          </FieldWrapper>

          {/* Category Photo */}
          <FieldWrapper>
            <FieldGroup>
            <FieldTitle>{t('categoryPhoto')}</FieldTitle>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileChange(e, false, 'categoryPhoto')}
            />
            </FieldGroup>

              {isLoading ? (
                 <p>Loading...</p>
               ) : form.categoryPhoto || form.existingCategoryFileName ? (
                 <SinglePhotoContainer>
                   <img
                     src={
                       form.categoryPhoto
                         ? URL.createObjectURL(form.categoryPhoto)
                         : form.categoryBase64Src
                     }
                     alt="Category"
                     style={{ width: 120, height: 120, objectFit: 'cover', cursor: 'pointer' }}
                     onClick={() =>
                       handleOpenModal(
                         form.categoryPhoto
                           ? URL.createObjectURL(form.categoryPhoto)
                           : form.categoryBase64Src
                       )
                     }
                   />
                   <DeleteButton type="button" onClick={handleDeleteCategoryPhoto}>
                     {t('delete')}
                   </DeleteButton>
                 </SinglePhotoContainer>
               ) : (
                 <p>{t('noFiles')}</p> 
               )}
          </FieldWrapper>

          {/* Gallery Photos */}
          <FieldWrapper>
            <FieldGroup>
            <FieldTitle>{t('photos')}</FieldTitle>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={(e) => handleFileChange(e, true, 'photos')}
            />
            </FieldGroup>
            

             {isLoading ? (
              <p>Loading...</p>
               ) : form.photos.length === 0 && (!form.galleryBase64Srcs || form.galleryBase64Srcs.length === 0) ? (
                 <p>{t('noFiles')}</p> 
               ) : (
              <PhotoGrid>
                {/* New gallery photos */}
                {form.photos.map((photo, index) => (
                  <PhotoItem key={`new-${index}`}>
                    <img
                      src={URL.createObjectURL(photo)}
                      alt={`Gallery new ${index}`}
                      style={{ width: 100, height: 100, objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => handleOpenModal(URL.createObjectURL(photo))}
                    />
                    <DeleteButton type="button" onClick={() => handleDeleteNewGalleryPhoto(index)}>
                      {t('delete')}
                    </DeleteButton>
                  </PhotoItem>
                ))}
          
                {/* Existing gallery photos */}
                {form.galleryBase64Srcs?.map((src, index) => (
                  <PhotoItem key={`existing-${index}`}>
                    <img
                      src={src}
                      alt={`Gallery existing ${index}`}
                      style={{ width: 100, height: 100, objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => handleOpenModal(src)}
                    />
                    <DeleteButton type="button" onClick={() => handleDeleteExistingGalleryPhoto(index)}>
                      {t('delete')}
                    </DeleteButton>
                  </PhotoItem>
                ))}
              </PhotoGrid>
            )}
          
  
          </FieldWrapper>



          <Button type="submit">{t('save')}</Button>
        </FormWrapper>
      </form>

    
      <Modal visible={showModal} onClick={handleCloseModal}>
        <ModalImage src={modalImage} alt="Full screen view" onClick={(e) => e.stopPropagation()} />
      </Modal>
     
    </Container>
  );
};

export default EditExperience;



